import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

interface ClientLogo {
  name: string;
  logo: string;
  link?: string;
}

const clients: ClientLogo[] = [
  // i18n-exempt-start: client company names
  { name: 'Markcopy', logo: 'https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/692d6394a23762baf1ff05b8/42c99e922_63f8790600ef248f9eab7612_Markcopy-logo.png' },
  { name: 'DejaBlue', logo: 'https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/692d6394a23762baf1ff05b8/bea278291_DejaBlue.png' },
  { name: 'Morning', logo: 'https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/692d6394a23762baf1ff05b8/8a7b89860_Logo_Morning.png' },
  { name: 'Welcome to the Jungle', logo: 'https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/692d6394a23762baf1ff05b8/bde794a92_WTTJ_LOGO_2020.png' },
  { name: 'Mooncard', logo: 'https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/692d6394a23762baf1ff05b8/0d6a2de72_mooncard-logo-black.png' },
  { name: 'Greenscope', logo: 'https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/692d6394a23762baf1ff05b8/3c9209497_4ad2eed6-profile_Logo_Green_Horizontal_PNG_Original.png' },
  { name: 'Noé Gestion Privée', logo: 'https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/692d6394a23762baf1ff05b8/22aadfd18_Screenshot_20251205-174151-removebg-preview.png' },
  { name: 'Murmuse', logo: 'https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/692d6394a23762baf1ff05b8/7d6b4835f_logo-dark.png' },
  { name: 'Rocketforsales', logo: 'https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/692d6394a23762baf1ff05b8/8c6d5bba2_Rocketforsales.png' },
  { name: 'SideCare', logo: 'https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/692d6394a23762baf1ff05b8/cf90947ee_logo_bordeaux-139f05bc7c4822155ce880a28fc15dfd06903bae69da1e2c808863e69a8f208a.png' },
  { name: 'Fincome', logo: 'https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/692d6394a23762baf1ff05b8/cc480046b_672a407de2f145dc54b67c96_fincome_logo.png' },
  { name: 'Leverage', logo: 'https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/692d6394a23762baf1ff05b8/51ae30eec_Leveragelogo.png' },
  { name: 'Growth Hiring', logo: 'https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/692d6394a23762baf1ff05b8/d87c559a6_ZulsU7VsGrYSvbU-_Frame10000018521.png' },
  // i18n-exempt-end
];

export default function ClientsSection() {
  const { t } = useTranslation(['admin']);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [isInView, setIsInView] = useState(true);

  // Respect reduced motion
  useEffect(() => {
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setPrefersReducedMotion(mql.matches);
    onChange();
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);

  // Track small screens for lighter animation
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const onChange = () => setIsSmallScreen(mq.matches);
    onChange();
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  // Pause animation when section is offscreen
  useEffect(() => {
    if (!containerRef.current || !('IntersectionObserver' in window)) return;
    const ob = new IntersectionObserver((entries) => {
      setIsInView(entries[0]?.isIntersecting !== false);
    }, { threshold: 0 });
    ob.observe(containerRef.current);
    return () => ob.disconnect();
  }, []);

  const groupCount = useMemo(() => (isSmallScreen ? 2 : 3), [isSmallScreen]);
  const duration = useMemo(() => (isSmallScreen ? 26 : 20), [isSmallScreen]);
  const groups = useMemo(
    () => Array.from({ length: prefersReducedMotion ? 1 : groupCount }, (_, i) => i),
    [groupCount, prefersReducedMotion]
  );

  return (
    <section className="relative py-20 bg-[#0a0a0a] overflow-hidden">
      {/* Gradient line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-white/30">
            {t('admin:landing.clients.trustedBy')}
          </span>
        </motion.div>
      </div>

      {/* Scrolling container */}
      <div className="relative">
        {/* Fade edges */}
        <div className="absolute left-0 top-0 bottom-0 w-12 sm:w-24 md:w-32 bg-gradient-to-r from-[#0a0a0a] to-transparent z-10" />
        <div className="absolute right-0 top-0 bottom-0 w-12 sm:w-24 md:w-32 bg-gradient-to-l from-[#0a0a0a] to-transparent z-10" />

        <div ref={containerRef} className="flex">
          {groups.map((groupIndex) => (
            <motion.div
              key={groupIndex} aria-hidden={groupIndex !== 0}
              animate={isInView && !prefersReducedMotion ? { x: [0, '-100%'] } : { x: 0 }}
              transition={{
                x: { repeat: Infinity, repeatType: 'loop', duration, ease: 'linear' },
              }}
              style={{ willChange: 'transform' }}
              className="flex items-center gap-8 md:gap-16 flex-shrink-0 px-6 md:px-12"
            >
              {clients.map((client, index) => (
                <div
                  key={`${client.name}-${groupIndex}-${index}`}
                  className="flex-shrink-0 flex items-center justify-center"
                >
                  {client.link ? (
                    <a href={client.link} target="_blank" rel="noopener noreferrer">
                      <img
                        src={client.logo}
                        alt={client.name}
                        loading="lazy"
                        decoding="async"
                        sizes="(max-width: 768px) 120px, 180px"
                        className={`w-auto object-contain opacity-60 hover:opacity-80 transition-opacity duration-300 brightness-0 invert grayscale cursor-pointer ${ (client.name === 'Murmuse' || client.name === 'Mutmuse') ? 'h-8 sm:h-10 md:h-12 lg:h-14 max-w-[160px] md:max-w-[220px]' : 'h-6 sm:h-8 md:h-10 lg:h-12 max-w-[140px] md:max-w-[180px]'}`}
                      />
                    </a>
                  ) : (
                    <img
                      src={client.logo}
                      alt={client.name}
                      loading="lazy"
                      decoding="async"
                      sizes="(max-width: 768px) 120px, 180px"
                      className={`w-auto object-contain opacity-60 hover:opacity-80 transition-opacity duration-300 brightness-0 invert grayscale ${ (client.name === 'Murmuse' || client.name === 'Mutmuse') ? 'h-8 sm:h-10 md:h-12 lg:h-14 max-w-[160px] md:max-w-[220px]' : 'h-6 sm:h-8 md:h-10 lg:h-12 max-w-[140px] md:max-w-[180px]'}`}
                    />
                  )}
                </div>
              ))}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}